import { useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import {
  compact,
  difference,
  every,
  filter,
  find,
  includes,
  isEmpty,
  map,
  uniq,
} from "lodash/fp";
import { DateTime } from "luxon";

import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import {
  ProductivityProviderId,
  ProductivityIntegrationStatus,
  User,
  ProductivityIntegrationStatusStep,
} from "@app/common/types";
import Config from "@app/config";

const EMPTY_STATUSES: ProductivityIntegrationStatus[] = [];
const TWO_MINUTES = 2 * 60 * 1000;
const RELEASE_DATE = DateTime.fromFormat(
  Config.releaseDates.initialReadoutExperience,
  "MM/dd/yyyy"
);

export interface UseProductivityIntegrationStatus {
  isLoading: boolean;
  processingProvider?: ProductivityIntegrationStatus["provider"];
  statuses: ProductivityIntegrationStatus[];
  onDismissProviderStatus: (provider: ProductivityProviderId) => void;
  dismissedReadoutStatusProviders: ProductivityProviderId[];
}

const getQueryKey = (user: User | null) =>
  compact(["integrations", "status", user?.id]);

const providerWasConnectedRecently = (date?: DateTime) => {
  if (!date) return false;
  const twoWeeksAgo = DateTime.now().minus({ weeks: 2 });
  // provider must have been connected within the last 2 weeks AND after the
  // initial release of this experience
  const compareDate = DateTime.max(RELEASE_DATE, twoWeeksAgo);
  return date > compareDate;
};

const getProcessingProvider = (
  statuses: ProductivityIntegrationStatus[]
): ProductivityIntegrationStatus["provider"] | undefined => {
  if (!Config.flags.enableInitialReadoutExperience) return;

  const hasARealReadout =
    filter((s) => !isEmpty(s.topicReportIds), statuses).length > 0;

  if (hasARealReadout) return;

  const firstProcessingProvider = find(
    (s) => s.step !== ProductivityIntegrationStatusStep.COMPLETE,
    statuses
  );
  return firstProcessingProvider?.provider;
};

const useProductivityIntegrationStatus =
  (): UseProductivityIntegrationStatus => {
    const { user } = useAuth();
    const {
      isLoading: integrationsLoading,
      isProviderConnected,
      userIntegrations,
    } = useIntegrations();
    const [shouldPollForUpdates, setShouldPollForUpdates] = useState(true);

    const [
      dismissedReadoutStatusProviders,
      setDismissedReadoutStatusProviders,
    ] = useLocalStorage<ProductivityProviderId[]>(
      "dismissedReadoutStatusProviders",
      []
    );

    const query = useQuery({
      queryKey: getQueryKey(user),
      queryFn: apiClient.integrations.listIntegrationStatuses,
      enabled: Boolean(user?.id) && Config.flags.enableInitialReadoutExperience,
      refetchOnWindowFocus: shouldPollForUpdates,
      refetchOnMount: shouldPollForUpdates,
      // use interval consistent with the refetch interval for reports
      refetchInterval: shouldPollForUpdates ? TWO_MINUTES : undefined,
    });

    const rawStatuses = query.data ?? EMPTY_STATUSES;

    const connectedProviderStatuses: ProductivityIntegrationStatus[] =
      useMemo(() => {
        if (isEmpty(userIntegrations)) return EMPTY_STATUSES;

        // the status endpoint returns the status of all potential providers,
        // but we only care about the ones that:
        // 1) are currently connected
        // 2) were connected after the initial readout experience feature was released
        // 3) have not already been dismissed
        return filter(
          (status) =>
            providerWasConnectedRecently(status.createdOn) &&
            isProviderConnected({
              providerId: status.provider,
              scope: "user",
            }) &&
            !includes(status.provider, dismissedReadoutStatusProviders),
          rawStatuses
        );
      }, [rawStatuses, userIntegrations, dismissedReadoutStatusProviders]);

    const allConnectedProvidersHaveBeenDismissed = isEmpty(
      difference(
        map("provider", connectedProviderStatuses),
        dismissedReadoutStatusProviders
      )
    );

    const allProvidersHaveCompletedProcessing = every(
      (s) => s.step === ProductivityIntegrationStatusStep.COMPLETE,
      connectedProviderStatuses
    );

    useEffect(() => {
      // disable polling once the user has dismissed all the modules or if there's
      // no more status updates left
      if (
        allConnectedProvidersHaveBeenDismissed &&
        allProvidersHaveCompletedProcessing
      ) {
        setShouldPollForUpdates(false);
      }
    }, [
      allConnectedProvidersHaveBeenDismissed,
      allProvidersHaveCompletedProcessing,
    ]);

    const handleDismissProviderStatus = (
      providerId: ProductivityProviderId
    ) => {
      setDismissedReadoutStatusProviders(
        uniq([...dismissedReadoutStatusProviders, providerId])
      );
    };

    return {
      isLoading: query.isLoading || integrationsLoading,
      processingProvider: getProcessingProvider(connectedProviderStatuses),
      statuses: connectedProviderStatuses,
      onDismissProviderStatus: handleDismissProviderStatus,
      dismissedReadoutStatusProviders,
    };
  };

export default useProductivityIntegrationStatus;
